import { useContext, useState } from "react";
import { C } from "./theme";
import { Logo } from "./Logo";
import { TOP_BAR_HEIGHT } from "./layout";
import { TopBarContext } from "./TopBarContext";
import { HelpSheet } from "./HelpSheet";
import { BackButton } from "./BackButton";
import { useWideScreen } from "./useWideScreen";

const icon = (children, strokeWidth = 2) => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
export const BAR_ICONS = {
  add: icon(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>, 2.5),
  search: icon(<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  nudges: icon(<><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>),
  settings: icon(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>),
};

// Each button is a 44px tap area around a smaller 36px face (Craig, 20 Oct 2026: "reduce the size of the
// buttons a bit"). `round` is the ? button's green circle.
// `flat` is the sidebar's (its Nudges and Settings): no box, like its rows, and `current` washes it in the accent on the
// page showing, as the rows are.
export function BarButton({ label, onClick, round = false, flat = false, current = false, expanded, children }) {
  const face = round
    ? { borderRadius: "50%", border: `1.5px solid ${C.textGreen}`, background: C.accentLight, color: C.textGreen, fontSize: 16, fontWeight: 700 }
    : flat
      ? { borderRadius: 10, background: current ? C.accentLight : "transparent", color: current ? C.accent : C.textMid }
      : { borderRadius: 10, border: `1px solid ${C.border}`, background: C.surface2, color: C.textMid };
  return (
    <button onClick={onClick} aria-label={label} title={label} aria-current={current ? "page" : undefined} aria-expanded={expanded} className={flat ? "side-icon" : undefined}
      style={{ width: 44, height: 44, flexShrink: 0, display: "grid", placeItems: "center", padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }}>
      <span style={{ width: 36, height: 36, boxSizing: "border-box", display: "grid", placeItems: "center", ...face }}>{children}</span>
    </button>
  );
}

// The bar at the top of every page but Settings: ◀ back to Home and the page's name (the Nudge logo on Home), then
// + (add), search, Nudges, ? (how this page works) and Settings. Craig, 20 Oct 2026. `extra` is a page's own
// button, placed before +: Chat's "Start a new chat".
// `help` is { title, lines } from helpContent.js. + opens the same "Add new" sheet on every page (Craig, 24 Oct
// 2026: until then Dreams, Fears and Key Dates went straight to their own editor).
// `titleAs` is the title's element: "h1", or "div" where the page under the bar has its own h1 (Learn).
// On a wide screen the sidebar holds the logo, +, search, Nudges and Settings, so the bar keeps only the page's name, its
// own button and ?; Home is named "Home" there.
export function TopBar({ title, titleAs: Title = "h1", logo = false, help, extra = null }) {
  const actions = useContext(TopBarContext);
  const [helpOpen, setHelpOpen] = useState(false);
  const wide = useWideScreen();
  return (
    // A faint line under the bar marks where the page scrolls beneath it.
    <header style={headerStyle()}>
      {logo && !wide
        ? <Logo height={40} />
        : (
          <PageTitle title={logo ? "Home" : title} titleAs={logo ? "div" : Title} onBack={actions.onHome} />
        )}
      <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
        {extra}
        {!wide && <BarButton label="Add new" onClick={actions.onAdd}>{BAR_ICONS.add}</BarButton>}
        {!wide && <BarButton label="Search" onClick={actions.onSearch}>{BAR_ICONS.search}</BarButton>}
        {!wide && <BarButton label="Nudges" onClick={actions.onNudges}>{BAR_ICONS.nudges}</BarButton>}
        {help && <BarButton label={help.title} onClick={() => setHelpOpen(true)} round>?</BarButton>}
        {!wide && <BarButton label="Settings" onClick={actions.onSettings}>{BAR_ICONS.settings}</BarButton>}
      </div>
      {helpOpen && <HelpSheet title={help.title} lines={(wide && help.wideLines) || help.lines} onClose={() => setHelpOpen(false)} />}
    </header>
  );
}

const headerStyle = () => ({ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, padding: "6px 4px 6px 16px", minHeight: TOP_BAR_HEIGHT, boxSizing: "border-box", background: C.bg, borderBottom: `1px solid ${C.border}` });

// ◀ and the page's name, as every bar draws them. `backLabel` says where ◀ goes when it isn't Home.
export function PageTitle({ title, titleAs: Title = "h1", onBack, backLabel }) {
  return (
    <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      <BackButton onClick={onBack} label={backLabel} />
      {/* Up to 20px, and smaller on a narrow phone, so the longest names ("Temptations", "Dreamboard") stay on one
          line beside ◀ and the five buttons at 375px (Craig, 24 Oct 2026, added ◀). */}
      <Title style={{ margin: 0, fontSize: "clamp(17px, 4.6vw, 20px)", fontWeight: 800, letterSpacing: "-0.015em", lineHeight: 1.1, color: C.text, whiteSpace: "nowrap" }}>{title}</Title>
    </div>
  );
}

// The bar of a page that goes back where it came from rather than Home (the Goal page, Get Support, Nudge Pro):
// the same ◀, name, height and line as TopBar, with the page's own buttons on the right (`children`).
export function PageHeader({ title, onBack, backLabel, children = null }) {
  return (
    <header style={{ ...headerStyle(), paddingRight: 16 }}>
      <PageTitle title={title} onBack={onBack} backLabel={backLabel} />
      {children}
    </header>
  );
}
